import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { CaretDownIcon } from "@/modules/common/assets/icons";
import { Button, CheckboxV2, Modal, ModalFooter, Spinner } from "@/modules/common/components";
import { cn } from "@/libs/cn";
import { DemoTemplateModuleResponse } from "@/service/response/admin-management-companies";
import useDemoTemplateModules from "../../../../hooks/useDemoTemplateModules";
import { addWithDependencies, namesOf, removeWithDependents, resolveDemoModules } from "../../../../utils/demoModules";

interface ConfigureDemoModulesModalProps {
  open: boolean;
  selectedModules: string[];
  onClose: () => void;
  onSave: (modules: string[]) => void;
}

const ConfigureDemoModulesModal = ({ open, selectedModules, onClose, onSave }: ConfigureDemoModulesModalProps) => {
  const { data: modules, isLoading } = useDemoTemplateModules(open);
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedModules));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(resolveDemoModules(selectedModules, modules ?? [])));
    setExpanded(new Set());
  }, [open, modules]);

  const handleToggleExpanded = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const handleToggle = (module: DemoTemplateModuleResponse, checked: boolean) => {
    if (module.required) return;

    setSelected((current) => {
      const next = new Set(current);

      if (checked) {
        addWithDependencies(module.key, modules ?? [], next);

        const autoAdded = [...next].filter((key) => key !== module.key && !current.has(key));
        if (autoAdded.length > 0) {
          toast.info(`Selecionado(s) automaticamente: ${namesOf(autoAdded, modules ?? []).join(", ")} (necessário para ${module.name}).`);
        }
      } else {
        removeWithDependents(module.key, modules ?? [], next);

        const autoRemoved = [...current].filter((key) => key !== module.key && !next.has(key));
        const autoAdded = [...next].filter((key) => !current.has(key));

        if (autoRemoved.length > 0) {
          toast.info(`Removido(s) automaticamente: ${namesOf(autoRemoved, modules ?? []).join(", ")} (dependiam de ${module.name}).`);
        }
        if (autoAdded.length > 0) {
          toast.info(
            `Selecionado(s) automaticamente: ${namesOf(autoAdded, modules ?? []).join(", ")} (substitui ${module.name} como alternativa).`,
          );
        }
      }

      return next;
    });
  };

  const handleSave = () => {
    onSave(Array.from(selected));
    onClose();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Configuração da Demonstração"
      description="Marque os módulos e cenários que deverão ser disponibilizados automaticamente nesta Gestora Demo."
      className="max-w-2xl"
    >
      {isLoading ? (
        <Spinner message="Carregando módulos..." />
      ) : (
        <div className="flex max-h-[60vh] flex-col divide-y divide-ui-border overflow-y-auto">
          {modules?.map((module) => {
            const isChecked = module.required || selected.has(module.key);
            const isExpanded = expanded.has(module.key);

            return (
              <div key={module.key} className="flex flex-col py-4 first:pt-0 last:pb-0">
                <div className="flex items-center gap-3">
                  <CheckboxV2
                    checked={isChecked}
                    disabled={module.required || !module.available}
                    onCheckedChange={(checked) => handleToggle(module, checked === true)}
                  />

                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left"
                    aria-expanded={isExpanded}
                    onClick={() => handleToggleExpanded(module.key)}
                  >
                    <p className="text-sm font-medium text-ui-text">{module.name}</p>
                    {module.required && <span className="text-xs text-ui-text-muted">(obrigatório)</span>}
                    <CaretDownIcon
                      className={cn(
                        "ml-auto size-3.5 shrink-0 text-ui-text-muted transition-transform duration-200",
                        isExpanded && "rotate-180",
                      )}
                    />
                  </button>
                </div>

                {isExpanded && (
                  <div className="ml-8 mt-2 flex flex-col gap-2">
                    {module.description && <p className="text-sm text-ui-text-muted">{module.description}</p>}

                    {module.requiredDependencies.length > 0 && (
                      <p className="text-xs text-ui-text-muted">Requer: {namesOf(module.requiredDependencies, modules ?? []).join(", ")}</p>
                    )}

                    {module.alternativeDependencies.length > 0 && (
                      <p className="text-xs text-ui-text-muted">
                        Requer um de: {namesOf(module.alternativeDependencies, modules ?? []).join(" ou ")}
                      </p>
                    )}

                    {module.items.length > 0 && (
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-ui-text">Itens e observações:</p>
                        <ul className="list-disc pl-5">
                          {module.items.map((item) => (
                            <li key={item} className="text-sm text-ui-text-muted">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ModalFooter>
        <Button className="w-full" type="button" variant="outlined" label="Cancelar" onClick={onClose} />
        <Button className="w-full" type="button" label="Salvar seleção" onClick={handleSave} disabled={isLoading} />
      </ModalFooter>
    </Modal>
  );
};

export default ConfigureDemoModulesModal;
